import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { receiptService } from '../../../../apiServices/receiptService';
import { PositionSuggestionResponse } from '../../../../apiServices/types';
import './PositionSuggestionsPanel.css';

interface PositionSuggestionsPanelProps {
  value: string;
  categoryId?: string;
  onSelect: (value: string, shiftKey?: boolean) => void;
  onHighlightChange?: (highlightedSuggestion: PositionSuggestionResponse | null) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const PositionSuggestionsPanel: React.FC<PositionSuggestionsPanelProps> = ({
  value,
  categoryId,
  onSelect,
  onHighlightChange,
  inputRef
}) => {
  const [suggestions, setSuggestions] = useState<PositionSuggestionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; isAbove: boolean; arrowPosition?: 'top' | 'bottom' | 'left' | 'right' }>({ 
    top: 0, 
    left: 0, 
    width: 0, 
    isAbove: false,
    arrowPosition: 'bottom'
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const onHighlightChangeRef = useRef(onHighlightChange);
  
  // Оновити ref при зміні callback
  useEffect(() => {
    onHighlightChangeRef.current = onHighlightChange;
  }, [onHighlightChange]);

  // Завантажити підказки при зміні значення
  useEffect(() => {
    // Якщо значення порожнє, не показувати підказки
    if (!value || value.length < 1) {
      setSuggestions([]);
      setIsVisible(false);
      return;
    }

    const loadSuggestions = async () => {
      try {
        setIsLoading(true);
        const loadedSuggestions = await receiptService.getPositionSuggestions(value, categoryId, 8);
        setSuggestions(loadedSuggestions);
        setIsVisible(loadedSuggestions.length > 0);
      } catch (error) {
        console.error('Помилка завантаження підказок позицій:', error);
        setSuggestions([]);
        setIsVisible(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuggestions();
  }, [value, categoryId]);

  // Оновити позицію панелі при зміні видимості або скролу
  useEffect(() => {
    const updatePosition = () => {
      if (!inputRef.current || !isVisible) return;

      const inputRect = inputRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      const panelWidth = 280;
      const panelMaxHeight = 400;
      const isMobile = window.innerWidth <= 768;
      const spacing = isMobile ? 4 : 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let top = 0;
      let left = 0;
      let width = panelWidth;
      
      let isAbove = false;
      let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
      
      if (isMobile) {
        // Мобільна версія: завжди під/над інпутом
        width = Math.min(panelWidth, viewportWidth - spacing * 2);
        left = inputRect.left + scrollX;
        
        const spaceBelow = viewportHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;
        
        if (spaceBelow >= 300 || spaceBelow >= spaceAbove) {
          // Показувати знизу
          top = inputRect.bottom + scrollY + spacing;
          isAbove = false;
          arrowPosition = 'top';
        } else {
          // Показувати зверху - верхній край хінта на верхньому краю інпута
          top = inputRect.top + scrollY;
          isAbove = true;
          arrowPosition = 'bottom';
        }
      } else {
        // Десктопна версія: завжди під/над інпутом
        left = inputRect.left + scrollX;
        width = Math.min(panelWidth, inputRect.width);
        
        const spaceBelow = viewportHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;
        
        if (spaceBelow >= 300 || spaceBelow >= spaceAbove) {
          // Показувати знизу
          top = inputRect.bottom + scrollY + spacing;
          arrowPosition = 'top';
          isAbove = false;
        } else {
          // Показувати зверху
          top = inputRect.top + scrollY;
          arrowPosition = 'bottom';
          isAbove = true;
        }
      }
      
      // Переконатися що не виходить за межі екрану
      if (left < scrollX + spacing) {
        left = scrollX + spacing;
      }
      if (left + width > scrollX + viewportWidth - spacing) {
        width = viewportWidth - (left - scrollX) - spacing * 2;
      }
      if (top < scrollY + spacing) {
        top = scrollY + spacing;
      }
      
      setPosition({
        top,
        left,
        width,
        isAbove,
        arrowPosition
      });
    };

    updatePosition();

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, suggestions.length, inputRef]);

  // Скинути highlightedIndex при зміні suggestions (тільки коли дійсно змінилися дані)
  const previousSuggestionsRef = useRef<PositionSuggestionResponse[]>([]);
  useEffect(() => {
    // Перевірити чи дійсно змінилися suggestions (порівняти по вмісту, а не по посиланню)
    const suggestionsChanged = JSON.stringify(previousSuggestionsRef.current) !== JSON.stringify(suggestions);
    
    if (suggestionsChanged) {
      setHighlightedIndex(-1);
      itemRefs.current = [];
      if (onHighlightChangeRef.current) {
        onHighlightChangeRef.current(null);
      }
      previousSuggestionsRef.current = suggestions;
    }
  }, [suggestions]);

  // Повідомляти про зміну активного хінта
  useEffect(() => {
    if (onHighlightChangeRef.current) {
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        onHighlightChangeRef.current(suggestions[highlightedIndex]);
      } else {
        onHighlightChangeRef.current(null);
      }
    }
  }, [highlightedIndex, suggestions]);

  // Автоматичний скрол до виділеного елемента
  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex] && listRef.current) {
      const highlightedElement = itemRefs.current[highlightedIndex];
      const listContainer = listRef.current;
      
      // Невелика затримка для того, щоб DOM оновився
      setTimeout(() => {
        if (!highlightedElement || !listContainer) return;
        
        const containerRect = listContainer.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();
        
        // Розрахувати чи елемент повністю видимий
        const elementTop = elementRect.top;
        const elementBottom = elementRect.bottom;
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;
        
        const isAboveViewport = elementTop < containerTop;
        const isBelowViewport = elementBottom > containerBottom;
        
        // Якщо елемент не повністю видимий - прокрутити до нього
        if (isAboveViewport || isBelowViewport) {
          // Використати scrollIntoView для автоматичного скролу
          highlightedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 0);
    }
  }, [highlightedIndex]);

  // Обробка клавіатури для навігації
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || suggestions.length === 0) return;

      // Перевірити чи фокус на інпуті
      if (document.activeElement !== inputRef.current) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          // Якщо є виділений хінт, вибрати його (як з Enter, так і з SHIFT + ENTER)
          if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
            e.preventDefault();
            e.stopPropagation();
            onSelect(suggestions[highlightedIndex].name, e.shiftKey);
            setIsVisible(false);
            setHighlightedIndex(-1);
          }
          // Якщо немає виділеного хінта, не перешкоджати стандартній обробці Enter (відправка форми)
          // SHIFT + ENTER буде оброблено в CategoryCard для встановлення правильного фокусу
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsVisible(false);
          setHighlightedIndex(-1);
          // Не видаляти фокус з інпута, залишити його активним
          break;
      }
    };

    if (isVisible) {
      // Використовуємо capture phase щоб обробник спрацював перед onKeyDown в інпуті
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isVisible, suggestions, highlightedIndex, onSelect, inputRef]);

  // Закрити панель при кліку поза нею
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
        setHighlightedIndex(-1);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, inputRef]);

  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  const isMobile = window.innerWidth <= 768;
  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: position.top,
    left: position.left,
    width: position.width,
    zIndex: 9999999,
    transform: position.isAbove ? 'translateY(-100%)' : 'none',
    marginTop: position.isAbove ? -4 : 0
  };

  return createPortal(
    <div 
      ref={panelRef} 
      className={`position-suggestions-panel ${isMobile ? 'mobile' : ''}`}
      style={panelStyle}
    >
      {isLoading ? (
        <div className="position-suggestions-loading">
          Завантаження...
        </div>
      ) : (
        <>
          <div className="position-suggestions-header">
            <span>Підказки</span>
          </div>
          <div ref={listRef} className="position-suggestions-list">
            {suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.name}-${suggestion.categoryId || 'no-category'}-${index}`}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`position-suggestion-item ${
                  index === highlightedIndex ? 'highlighted' : ''
                }`}
                onClick={() => {
                  onSelect(suggestion.name);
                  setIsVisible(false);
                  setHighlightedIndex(-1);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="position-suggestion-content">
                  <span className="position-suggestion-name">{suggestion.name}</span>
                  {suggestion.categoryName && (
                    <span className="position-suggestion-category">
                      {suggestion.categoryName}
                    </span>
                  )}
                </div>
                {suggestion.count > 1 && (
                  <span className="position-suggestion-count">
                    {suggestion.count}x
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );
};

export default PositionSuggestionsPanel;
